// src/pages/SuperAdmin/AdminPendentes.tsx
import React, { useEffect, useState, useCallback } from "react";
import { fetchUsuariosTemporarios } from "../../services/adminApi";
import { Search, HourglassEmpty } from "@mui/icons-material";

type UsuarioTemp = {
  uid_usuario_temporario: string;
  telefone: string;
  primeira_mensagem: string | null;
  data_primeiro_contato: string;
  status: string;
  uid_fazenda_sugerida: string | null;
  origem: string | null;
};

const statusConfig: Record<string, { bg: string; color: string; label: string }> = {
  aguardando_nome: { bg: "#fef3c7", color: "#d97706", label: "Aguardando Nome" },
  convertido:      { bg: "#e8f4ef", color: "#00b37e", label: "Convertido" },
  descartado:      { bg: "#fee2e2", color: "#ef4444", label: "Descartado" },
};

const formatTelefone = (t: string) => {
  const d = t.replace(/\D/g, "");
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t || "—";
};

const formatDataHora = (d: string) =>
  new Date(d).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const AdminPendentes: React.FC = () => {
  const [lista, setLista] = useState<UsuarioTemp[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const carregar = useCallback(() => {
    setLoading(true);
    fetchUsuariosTemporarios(debouncedSearch, filtroStatus)
      .then((data) => setLista(
        [...data].sort((a: UsuarioTemp, b: UsuarioTemp) =>
          new Date(b.data_primeiro_contato).getTime() - new Date(a.data_primeiro_contato).getTime()
        )
      ))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [debouncedSearch, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={{ padding: "32px 28px", maxWidth: 1100, width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 700, color: "#1a2e1e" }}>
          Usuários Temporários
        </h1>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
          {loading ? "Carregando..." : `${lista.length} registro(s) encontrado(s)`}
        </p>
      </div>

      {/* Info card */}
      <div style={{
        background: "#fef9ec", border: "1px solid #fde68a",
        borderRadius: 12, padding: "14px 18px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <HourglassEmpty style={{ color: "#d97706" }} />
        <span style={{ fontSize: "0.875rem", color: "#92400e" }}>
          Estes são contatos que iniciaram conversa no WhatsApp mas ainda não completaram o cadastro como usuários.
        </span>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#fff", border: "1px solid #d1d5db",
          borderRadius: 10, padding: "8px 14px", flex: 1, minWidth: 200,
        }}>
          <Search style={{ color: "#9ca3af", fontSize: 20 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por telefone ou mensagem..."
            style={{ border: "none", outline: "none", fontSize: "0.9rem", width: "100%", background: "transparent" }}
          />
        </div>
        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
          style={{
            padding: "8px 14px", borderRadius: 10, border: "1px solid #d1d5db",
            fontSize: "0.9rem", background: "#fff", cursor: "pointer",
          }}
        >
          <option value="">Todos os status</option>
          <option value="aguardando_nome">Aguardando Nome</option>
          <option value="convertido">Convertido</option>
          <option value="descartado">Descartado</option>
        </select>
      </div>

      {/* Tabela */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5ede8", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Carregando...</div>
        ) : lista.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Nenhum registro encontrado.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8faf9", borderBottom: "1px solid #e5ede8" }}>
                  {["Telefone", "Primeira Mensagem", "Status", "Origem", "Primeiro Contato"].map((h) => (
                    <th key={h} style={{
                      padding: "12px 16px", textAlign: "left",
                      fontSize: "0.75rem", fontWeight: 700,
                      color: "#374151", textTransform: "uppercase", letterSpacing: 0.5,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map((u) => {
                  const sc = statusConfig[u.status] || statusConfig.aguardando_nome;
                  return (
                    <tr key={u.uid_usuario_temporario} style={{ borderBottom: "1px solid #f3f4f6" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8faf9")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                    >
                      <td style={{ padding: "14px 16px", fontWeight: 600, color: "#1a2e1e", fontSize: "0.9rem" }}>
                        {formatTelefone(u.telefone)}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#6b7280", fontSize: "0.85rem", maxWidth: 260 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {u.primeira_mensagem || <span style={{ color: "#9ca3af" }}>—</span>}
                        </div>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{
                          background: sc.bg, color: sc.color,
                          borderRadius: 6, padding: "3px 10px",
                          fontSize: "0.75rem", fontWeight: 700,
                        }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", color: "#6b7280", fontSize: "0.85rem" }}>
                        {u.origem || <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#6b7280", fontSize: "0.85rem" }}>
                        {formatDataHora(u.data_primeiro_contato)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPendentes;